import React from 'react';
import { Button, Space, Tag, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined, HistoryOutlined } from '@ant-design/icons';
import { RM2Fee } from '../../types';

interface GetRM2FeeColumnsParams {
  t: (key: string) => string;
  onEdit: (record: RM2Fee) => void;
  onShowHistory: (record: RM2Fee) => void;
  onDelete: (id: number) => void;
  deletingId: number | null;
}

export const getRM2FeeColumns = ({
  t,
  onEdit,
  onShowHistory,
  onDelete,
  deletingId
}: GetRM2FeeColumnsParams) => [
  {
    title: t('vehicle.capacity'),
    dataIndex: 'capacityTons',
    key: 'capacityTons',
    render: (capacityTons?: number) => capacityTons ? `${capacityTons} ${t('tons')}` : t('all.capacities')
  },
  {
    title: t('fee.amount'),
    dataIndex: 'fee',
    key: 'fee',
    render: (fee: number) => new Intl.NumberFormat('vi-VN', { 
      style: 'currency', 
      currency: 'VND' 
    }).format(fee)
  },
  {
    title: t('district.list'),
    dataIndex: 'districts',
    key: 'districts',
    render: (districts: any[]) => (
      <div>
        {districts.length === 0 ? (
          <Tag color="orange">{t('no.districts.applies.to.all')}</Tag>
        ) : (
          districts.slice(0, 3).map(district => (
            <Tag key={district.id} color="blue">
              {district.districtName}, {district.provinceName}
            </Tag>
          ))
        )}
        {districts.length > 3 && (
          <Tag color="default">+{districts.length - 3} {t('more.districts')}</Tag>
        )}
      </div>
    )
  },
  {
    title: t('notes'),
    dataIndex: 'notes',
    key: 'notes',
    render: (notes?: string) => notes || '-'
  },
  {
    title: t('affected.date'),
    dataIndex: 'affectedDate',
    key: 'affectedDate',
    sorter: (a: RM2Fee, b: RM2Fee) => new Date(a.affectedDate).getTime() - new Date(b.affectedDate).getTime(),
    render: (value: string) => new Date(value).toLocaleDateString()
  },
  {
    title: t('created.by'),
    dataIndex: 'createdByUserName',
    key: 'createdByUserName'
  },
  {
    title: t('created.at'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: (date: string) => new Date(date).toLocaleDateString()
  },
  {
    title: t('actions'),
    key: 'actions',
    render: (_: any, record: RM2Fee) => (
      <Space>
        <Button 
          size="small" 
          icon={<EditOutlined />} 
          onClick={() => onEdit(record)}
        >
          {t('edit')}
        </Button>
        <Button 
          size="small" 
          icon={<HistoryOutlined />} 
          onClick={() => onShowHistory(record)}
        >
          {t('history')}
        </Button>
        <Popconfirm
          title={t('confirm.delete')}
          description={t('delete.rm2.fee.confirm')}
          okText={t('yes.delete')}
          cancelText={t('cancel')}
          okType="danger"
          onConfirm={() => onDelete(record.id)}
        >
          <Button 
            size="small" 
            danger 
            icon={<DeleteOutlined />} 
            loading={deletingId === record.id}
          >
            {t('delete')}
          </Button>
        </Popconfirm>
      </Space>
    )
  }
];

export const getRM2FeeHistoryColumns = (t: (key: string) => string) => [
  {
    title: t('action'),
    dataIndex: 'action',
    key: 'action',
    render: (action: string) => <Tag color={action === 'Created' ? 'green' : action === 'Updated' ? 'blue' : 'red'}>{t(action.toLowerCase())}</Tag>
  },
  {
    title: t('old.fee'),
    dataIndex: 'oldFee',
    key: 'oldFee',
    render: (fee: number | null) => fee ? new Intl.NumberFormat('vi-VN', { 
      style: 'currency', 
      currency: 'VND' 
    }).format(fee) : '-'
  },
  {
    title: t('new.fee'),
    dataIndex: 'newFee',
    key: 'newFee',
    render: (fee: number) => new Intl.NumberFormat('vi-VN', { 
      style: 'currency', 
      currency: 'VND' 
    }).format(fee)
  },
  {
    title: t('reason'),
    dataIndex: 'reason',
    key: 'reason',
    render: (reason?: string) => reason || '-'
  },
  {
    title: t('affected.date'),
    dataIndex: 'affectedDate',
    key: 'affectedDate',
    render: (value: string) => new Date(value).toLocaleDateString()
  },
  {
    title: t('changed.by'),
    dataIndex: 'changedByUserName',
    key: 'changedByUserName'
  },
  {
    title: t('changed.at'),
    dataIndex: 'changedAt',
    key: 'changedAt',
    render: (date: string) => new Date(date).toLocaleString()
  }
];
